import {getLocaleContent} from '@helper/locale-content.js';
import {useRouter} from 'next/router';
import Link from 'next/link';
import React, {useContext, useState, useEffect, useRef} from 'react';
import TNHContext from '@contexts/TNHContext';
import MenuItemList from '../../Home/MenuItemList';
import ChatMenu from '@components/Home/ChatMenu';
import {getInternalHref} from '@helper/seo/site';

function Header() {
    const {email, hotline, menuTop} = useContext(TNHContext);
    const {locale, asPath, push} = useRouter();
    const [activeMenu, setActiveMenu] = useState(null); // only hover state (desktop)
    const [activeMobileMenu, setActiveMobileMenu] = useState(null); // Track active parent menu on mobile
    const [currentMenuId, setCurrentMenuId] = useState(null); // matched by URL (no hover)
    const [searchValue, setSearchValue] = useState('');
    const [mobileSearchValue, setMobileSearchValue] = useState('');
    const [showFixedButtons, setShowFixedButtons] = useState(false);
    const [footerVisible, setFooterVisible] = useState(false);
    const [filterType, setFilterType] = useState(0); // 0=all,1=doctor,2=service,3=department
    const [showChatMenu, setShowChatMenu] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const menuRef = useRef(null);

    const normalizeUrl = url => (url ? url.replace(/\/+$/, '') : '');
    const normalizePath = p => (p || '').replace(/\/{2,}/g, '/');
    const getMenuPath = url => {
        try {
            return normalizePath(new URL(url, 'https://tnh.com.vn').pathname).replace(/^\/en(?=\/|$)/, '') || '/';
        } catch {
            return normalizePath(String(url || '').split(/[?#]/)[0]).replace(/^\/en(?=\/|$)/, '') || '/';
        }
    };
    const isMenuMatch = menuUrl => {
        if (!menuUrl) return false;
        const current = normalizeUrl(normalizePath(asPath.split(/[?#]/)[0]).replace(/^\/en(?=\/|$)/, '')) || '/';
        const target = normalizeUrl(getMenuPath(menuUrl)) || '/';

        if (
            target === '/goi-dich-vu-tiem-chung' &&
            (current === '/goi-dich-vu' ||
                current === '/goi-dich-vu-v2' ||
                current.startsWith('/goi-dich-vu-chi-tiet/'))
        ) {
            return true;
        }

        return target === '/' ? current === '/' : current === target || current.startsWith(`${target}/`);
    };

    const handleLocaleChange = newLocale => {
        if (newLocale === 'en') {
            push(asPath, asPath, {locale: 'en'});
        } else if (newLocale === 'vn') {
            push(asPath, asPath, {locale: 'vn'});
        }
    };

    const handleMobileMenuToggle = menuId => {
        setActiveMobileMenu(prev => (prev === menuId ? null : menuId)); // Toggle active menu
    };

    const handleSearchSubmit = e => {
        e.preventDefault();
        if (searchValue.trim()) {
            push({
                pathname: '/tim-kiem',
                query: {filterValue: searchValue, filterType},
            });
        }
    };

    const handleMobileSearchSubmit = e => {
        e.preventDefault();
        if (mobileSearchValue.trim()) {
            push({
                pathname: '/tim-kiem',
                query: {filterValue: mobileSearchValue, filterType},
            });
        }
    };

    const handleFilterSelect = value => {
        setFilterType(value);
    };

    // Helper to render current label
    const filterLabel = () => {
        switch (filterType) {
            case 1:
                return getLocaleContent(locale, 'page.slide-home.doctor');
            case 2:
                return getLocaleContent(locale, 'page.slide-home.service');
            case 3:
                return getLocaleContent(locale, 'page.slide-home.department');
            default:
                return getLocaleContent(locale, 'common.all');
        }
    };

    // Scroll to top
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const toggleChatMenu = e => {
        e.preventDefault();
        setShowChatMenu(prev => !prev);
    };

    // Watch scroll
    useEffect(() => {
        let timeout = null;

        const handleScroll = () => {
            if (timeout) return;
            timeout = setTimeout(() => {
                if (window.scrollY > window.innerHeight * 0.2) {
                    setShowFixedButtons(true);
                } else {
                    setShowFixedButtons(false);
                }
                timeout = null;
            }, 200);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Keep floating actions from covering QR codes and links in the footer.
    useEffect(() => {
        const footer = document.getElementById('footer');
        if (!footer || !('IntersectionObserver' in window)) return undefined;

        const observer = new IntersectionObserver(
            entries => setFooterVisible(entries[0]?.isIntersecting ?? false),
            {threshold: 0.05},
        );
        observer.observe(footer);

        return () => observer.disconnect();
    }, []);

    // Đóng khi click ra ngoài
    useEffect(() => {
        let timer = null;

        function handleClickOutside(e) {
            if (!menuRef.current) return;

            if (menuRef.current.contains(e.target)) {
                if (timer) clearTimeout(timer);

                timer = setTimeout(() => {
                    setShowChatMenu(false);
                }, 2000);
            } else {
                setShowChatMenu(false);
            }
        }

        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
            if (timer) clearTimeout(timer);
        };
    }, []);

    // MATCH CURRENT ROUTE WITHOUT TRIGGERING HOVER STATE
    useEffect(() => {
        if (menuTop && menuTop.length) {
            const matched = menuTop.find(
                menuItem => isMenuMatch(menuItem.url) || menuItem.child?.some(child => isMenuMatch(child.url)),
            );
            if (matched) {
                setCurrentMenuId(matched.web_category_id); // do NOT call setActiveMenu here
            } else {
                // Reset when no menu matches (e.g., homepage)
                setCurrentMenuId(null);
            }
        }
    }, [asPath, menuTop]);

    // Close mobile drawer on route change
    useEffect(() => {
        setMobileMenuOpen(false);
        setActiveMobileMenu(null);
        setActiveMenu(null);
    }, [asPath]);

    return (
        <>
            <header className="site-header" id="header">
                {/* <!-- Fixed button --> */}
                <div className={`fixed-buttons${footerVisible ? ' is-hidden' : ''}`}>
                    {showFixedButtons && (
                        <button
                            type="button"
                            className={`pulse scroll-top ${showFixedButtons ? 'show' : ''}`}
                            aria-label={locale === 'en' ? 'Back to top' : 'Về đầu trang'}
                            onClick={scrollToTop}>
                            <img src="/bw_image/ic_scroll-top.png" alt="" />
                        </button>
                    )}
                    <Link href="/dat-lich-kham">
                        <a className="pulse appointment" aria-label={getLocaleContent(locale, 'common.booking')}>
                            <img src="/bw_image/ic_action-appointment.png" alt="" />
                        </a>
                    </Link>
                    <a href={`tel:${hotline}`} className="pulse hotline" aria-label={`Hotline ${hotline}`}>
                        <img src="/bw_image/ic_hotline.png" alt="" />
                    </a>
                    {/* Chatbot button */}
                    <button
                        type="button"
                        className="pulse chatbot"
                        aria-label={locale === 'en' ? 'Open contact options' : 'Mở các kênh liên hệ'}
                        aria-expanded={showChatMenu}
                        onClick={e => {
                            setShowChatMenu(!showChatMenu);
                            e.stopPropagation();
                        }}>
                        <img src="/bw_image/ic_chatbot.png" alt="" />
                    </button>

                    {/* Popup Menu */}
                    {showChatMenu && (
                        <div ref={menuRef} className="chat-popup-wrapper">
                            <ChatMenu />
                        </div>
                    )}
                </div>
                <div className="m-fixed-buttons">
                    <div>
                        <a href={`tel:${hotline}`}>
                            <img src="/bw_image/call-hotline.svg" alt="" />
                            <span className="call-hotline-text">{getLocaleContent(locale, 'common.call-hotline')}</span>
                        </a>
                        <a
                            href="#"
                            onClick={e => {
                                e.preventDefault(); // ngăn href="#" nhảy trang
                                e.stopPropagation(); // chặn click lan ra document (outside click)
                                toggleChatMenu(e); // gọi hàm toggle menu
                            }}>
                            <img src="/bw_image/ic_chatbot.png" alt="Chatbot" />
                            <span className="find-doctor-text">
                                {getLocaleContent(locale, 'page.contact-form.contact')}
                            </span>
                        </a>
                        <Link href="/dat-lich-kham">
                            <a>
                                <img src="/bw_image/booking.svg" alt="" />
                                <span className="booking-text">{getLocaleContent(locale, 'common.booking')}</span>
                            </a>
                        </Link>
                        {showChatMenu && (
                            <div ref={menuRef} className="chat-popup-wrapper mobile">
                                <ChatMenu />
                            </div>
                        )}
                    </div>
                </div>

                <input
                    type="checkbox"
                    id="mnav-toggle"
                    hidden
                    aria-hidden="true"
                    checked={mobileMenuOpen}
                    onChange={event => setMobileMenuOpen(event.target.checked)}
                />
                <div className="site-header__inner">
                    <div className="container topbar">
                        <Link href="/">
                            <a className="logo">
                                <img src="/bw_image/logo.jpg" alt="TNH" />
                            </a>
                        </Link>

                        <div className="topbar__center">
                            <form className="search" role="search" onSubmit={handleSearchSubmit}>
                                <input
                                    className="search__input"
                                    type="text"
                                    value={searchValue}
                                    onChange={e => setSearchValue(e.target.value)}
                                    placeholder={getLocaleContent(locale, 'common.search-label')}
                                    aria-label={getLocaleContent(locale, 'common.search-label')}
                                />
                                <button className="search__btn" aria-label="Tìm" type="submit">
                                    <img className="icon" src="/bw_image/ic_search.svg" alt="" />
                                </button>
                            </form>
                            <div className="contacts">
                                <span className="contacts__item">
                                    <img className="icon" src="/bw_image/ic_mail.svg" alt="" />
                                    <a className="email" href={`mailto:${email}`}>
                                        {getLocaleContent(locale, 'common.email')}: {email}
                                    </a>
                                </span>
                                <span className="contacts__sep"></span>
                                <span className="contacts__item">
                                    <img className="icon" src="/bw_image/ic_phone.svg" alt="" />
                                    <a className="hotline" href={`tel:${hotline}`}>
                                        {getLocaleContent(locale, 'common.call-center')} {hotline}
                                    </a>
                                </span>
                            </div>
                        </div>

                        <Link href="/dat-lich-kham">
                            <a className="cta">
                                <img className="icon" src="/bw_image/ic_calendar.svg" alt="" />
                                {getLocaleContent(locale, 'common.booking')}
                            </a>
                        </Link>
                        <label
                            className="mbar"
                            htmlFor="mnav-toggle"
                            aria-label="Mở menu"
                            aria-controls="mnav-drawer"
                            aria-expanded={mobileMenuOpen}>
                            <img className="icon" src="/bw_image/ic_menu.svg" alt="" />
                        </label>
                    </div>
                </div>
                <div className="site-header__inner mobile-only">
                    <div className="container topbar topbar-mobile">
                        <div className="mnav__searchbar">
                            <div className="mselect" data-value={filterType}>
                                <button
                                    type="button"
                                    className="mselect__control"
                                    aria-haspopup="listbox"
                                    aria-expanded="false">
                                    <span className="mselect__label">{filterLabel()}</span>
                                    {/* <img className="caret" src="/bw_image/ic_caret_down_m2.svg" alt="" /> */}
                                    <img className="caret" src="/bw_image/ic_caret_down_m3.svg" alt="" />
                                </button>

                                <ul className="mselect__menu" role="listbox">
                                    <li
                                        role="option"
                                        data-value="0"
                                        className={filterType === 0 ? 'is-active' : ''}
                                        aria-selected={filterType === 0}
                                        onClick={() => handleFilterSelect(0)}>
                                        {getLocaleContent(locale, 'common.all')}
                                    </li>
                                    <li
                                        role="option"
                                        data-value="1"
                                        className={filterType === 1 ? 'is-active' : ''}
                                        aria-selected={filterType === 1}
                                        onClick={() => handleFilterSelect(1)}>
                                        {getLocaleContent(locale, 'page.slide-home.doctor')}
                                    </li>
                                    <li
                                        role="option"
                                        data-value="2"
                                        className={filterType === 2 ? 'is-active' : ''}
                                        aria-selected={filterType === 2}
                                        onClick={() => handleFilterSelect(2)}>
                                        {getLocaleContent(locale, 'page.slide-home.service')}
                                    </li>
                                    <li
                                        role="option"
                                        data-value="3"
                                        className={filterType === 3 ? 'is-active' : ''}
                                        aria-selected={filterType === 3}
                                        onClick={() => handleFilterSelect(3)}>
                                        {getLocaleContent(locale, 'page.slide-home.department')}
                                    </li>
                                </ul>
                            </div>
                            <div className="mnav__search">
                                <form className="search" role="search" onSubmit={handleMobileSearchSubmit}>
                                    <input
                                        className="search__input"
                                        type="text"
                                        value={mobileSearchValue}
                                        onChange={e => setMobileSearchValue(e.target.value)}
                                        placeholder={getLocaleContent(locale, 'common.search')}
                                        aria-label={getLocaleContent(locale, 'common.search') + 'Tìm kiếm'}
                                    />
                                    <button className="search__btn" aria-label="Tìm" type="submit">
                                        <img className="icon" src="/bw_image/ic_search.svg" alt="Tìm" />
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
                <nav className="navbar" aria-label={locale === 'en' ? 'Main navigation' : 'Điều hướng chính'}>
                    <div className="container navbar__inner">
                        <div className="navmenu">
                            {menuTop.map(menuItem => {
                                const hasChild = menuItem.child && menuItem.child.length > 0;
                                const isCurrent = currentMenuId === menuItem.web_category_id; // route match
                                const isHovered = activeMenu === menuItem.web_category_id; // hover state
                                return (
                                    <div
                                        className={`navmenu__item ${isHovered ? 'is-open' : ''} ${
                                            isCurrent ? 'is-current' : ''
                                        }`}
                                        key={menuItem.web_category_id}
                                        onMouseEnter={() => setActiveMenu(menuItem.web_category_id)}
                                        onMouseLeave={() => setActiveMenu(null)}>
                                        <Link href={getInternalHref(menuItem.url)}>
                                            <a
                                                className="navmenu__link"
                                                aria-current={isCurrent ? 'page' : undefined}
                                                aria-haspopup={hasChild ? 'true' : undefined}
                                                aria-expanded={hasChild ? isHovered : undefined}>
                                                {menuItem.web_category_name}
                                                {hasChild && (
                                                    <img
                                                        className="caret"
                                                        src={
                                                            // Show UP only while hovered (submenu open). Route match alone keeps DOWN.
                                                            isHovered
                                                                ? '/bw_image/ic_caret_up.svg'
                                                                : isCurrent
                                                                ? '/bw_image/ic_caret_down_current.svg'
                                                                : '/bw_image/ic_caret_down.svg'
                                                        }
                                                        alt={isHovered ? 'Caret up' : 'Caret down'}
                                                    />
                                                )}
                                            </a>
                                        </Link>
                                        {hasChild && isHovered && (
                                            <div className="navmenu__submenu">
                                                <MenuItemList
                                                    items={menuItem.child.map(childItem => (
                                                        <Link
                                                            href={getInternalHref(childItem.url)}
                                                            key={childItem.web_category_id}>
                                                            <a className="navmenu__sublink">
                                                                {childItem.web_category_name}
                                                            </a>
                                                        </Link>
                                                    ))}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="langs">
                            <button
                                type="button"
                                className={`flag ${locale === 'vn' ? 'active' : ''}`}
                                aria-label="Tiếng Việt"
                                aria-pressed={locale === 'vn'}
                                onClick={() => handleLocaleChange('vn')}>
                                <img src="/bw_image/ic_flag_vn.png" alt="Tiếng Việt" />
                            </button>
                            <button
                                type="button"
                                className={`flag ${locale === 'en' ? 'active' : ''}`}
                                aria-label="English"
                                aria-pressed={locale === 'en'}
                                onClick={() => handleLocaleChange('en')}>
                                <img src="/bw_image/ic_flag_gb.png" alt="English" />
                            </button>
                        </div>
                    </div>
                </nav>
                {/* MOBILE DRAWER */}
                <div className="mnav" id="mnav-drawer" aria-hidden={!mobileMenuOpen}>
                    {/* backdrop để đóng nhanh */}
                    <label className="mnav__backdrop" htmlFor="mnav-toggle"></label>

                    <aside className="mnav__panel">
                        {/* TOP BAR XANH */}
                        <div className="mnav__top">
                            <img className="mnav__logo" src="/bw_image/logo_m.svg" alt="TNH" />
                            <div className="mnav__langs">
                                <button
                                    type="button"
                                    className={`flag ${locale === 'vn' ? 'active' : ''}`}
                                    aria-label="Tiếng Việt"
                                    aria-pressed={locale === 'vn'}
                                    onClick={() => handleLocaleChange('vn')}>
                                    <img src="/bw_image/ic_flag_vn.png" alt="Tiếng Việt" />
                                </button>
                                <button
                                    type="button"
                                    className={`flag ${locale === 'en' ? 'active' : ''}`}
                                    aria-label="English"
                                    aria-pressed={locale === 'en'}
                                    onClick={() => handleLocaleChange('en')}>
                                    <img src="/bw_image/ic_flag_gb.png" alt="English" />
                                </button>
                            </div>
                            <label htmlFor="mnav-toggle" className="mnav__close" aria-label="Đóng menu">
                                <img className="icon" src="/bw_image/ic_close.svg" alt="" />
                            </label>
                        </div>
                        {/* MENU ITEMS */}
                        <nav className="mnav__menu">
                            {menuTop.map(menuItem => {
                                const hasChild = menuItem.child && menuItem.child.length > 0;
                                const isCurrent = currentMenuId === menuItem.web_category_id;
                                const isOpen = activeMobileMenu === menuItem.web_category_id;

                                return (
                                    <div
                                        className={`mnav__item ${isOpen || isCurrent ? 'is-active' : ''}`}
                                        key={menuItem.web_category_id}>
                                        <div className="mnav__item-header">
                                            <Link href={getInternalHref(menuItem.url)}>
                                                <a
                                                    className="mnav__link"
                                                    aria-current={isCurrent ? 'page' : undefined}
                                                    aria-expanded={hasChild ? isOpen : undefined}>
                                                    {menuItem.web_category_name}
                                                </a>
                                            </Link>
                                            {hasChild && (
                                                <button
                                                    type="button"
                                                    className="mnav__submenu-toggle"
                                                    aria-label={`${isOpen ? 'Thu gọn' : 'Mở rộng'} ${
                                                        menuItem.web_category_name
                                                    }`}
                                                    aria-expanded={isOpen}
                                                    onClick={() => handleMobileMenuToggle(menuItem.web_category_id)}>
                                                    <img
                                                        className="caret"
                                                        src={
                                                            isOpen
                                                                ? '/bw_image/ic_caret_up_m.svg'
                                                                : '/bw_image/ic_caret_down_m.svg'
                                                        }
                                                        alt=""
                                                    />
                                                </button>
                                            )}
                                        </div>
                                        {hasChild && isOpen && (
                                            <div className="mnav__submenu">
                                                {menuItem.child.map(childItem => {
                                                    const childCurrent = isMenuMatch(childItem.url);
                                                    return (
                                                        <Link
                                                            key={childItem.web_category_id}
                                                            href={getInternalHref(childItem.url)}>
                                                            <a
                                                                className={`mnav__sublink ${
                                                                    childCurrent ? 'is-current' : ''
                                                                }`}
                                                                aria-current={childCurrent ? 'page' : undefined}>
                                                                {childItem.web_category_name}
                                                            </a>
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </nav>
                        <hr className="mnav__divider" />

                        {/* CONTACTS */}
                        <div className="mnav__contacts">
                            <span className="contacts__item">
                                <img className="icon" src="/bw_image/ic_mail.svg" alt="" />
                                Email: {email}
                            </span>
                            <span className="contacts__item">
                                <img className="icon" src="/bw_image/ic_phone.svg" alt="" />
                                {getLocaleContent(locale, 'common.call-center') + ' ' + hotline}
                            </span>
                        </div>
                    </aside>
                </div>
            </header>
        </>
    );
}

export default React.memo(Header);
